// The chords panel: big diagrams of the chord being played and the one
// coming up, following the player through the song (by listening, or by
// taps and a page-turner pedal) with or without the backing track; and the
// song's chords to hear, with the first chord and a drone on the key to find
// the starting note by.

import React, { useEffect } from 'react';
import classNames from 'classnames';
import { parseChord } from '../backing-track/chords';
import { trackOnce } from '../../usage';
import { chordNotes } from './strum';
import { ChordDiagram, MicLevel, Segmented, micProblem } from './controls';
import { KeyHint, chordNumber, isShortcut, spaceLabel } from './shortcuts';

// The chord the player will play next: the one after the marker while
// they're followed through the song, else the song's first chord.
export function upcomingChord(track) {
    if (track.active && track.live.chord) return track.live.next;
    return track.chordNames[0] || null;
}

export default function ChordsPanel({ track, chordsByName, tuning, player, songKey, droneOn, onToggleDrone }) {
    const { live, active, playing, following, mode, settings } = track;
    const names = track.chordNames;
    const placed = active && live.chord;
    const now = placed ? live.chord : names[0];
    const next = placed ? live.next : names.find(name => name !== names[0]);
    const keyChord = parseChord(songKey);

    const notesOf = name => chordNotes(parseChord(name), chordsByName[name], tuning);
    const hear = (name, via = 'click') => {
        player.strum(notesOf(name));
        trackOnce('chord_strummed', { via }, via);
    };

    // 1–9 play the song's chords as numbered below. Following by tapping,
    // the same keys move the marker (see useBackingTrack), so playing a chord
    // also says where you are.
    useEffect(() => {
        const onKey = e => {
            const n = chordNumber(e.key);
            if (!n || e.repeat || !isShortcut(e, { evenIfHandled: true })) return;
            const name = track.songChords[n - 1];
            if (!name) return;
            e.preventDefault();
            hear(name, 'key');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    // Fetch the recorded notes of the song's chords before they're tapped.
    useEffect(() => {
        player.preload([...new Set(track.songChords.flatMap(notesOf))]);
    }, [track.songChords, chordsByName, tuning]); // eslint-disable-line react-hooks/exhaustive-deps

    let hint = null;
    if (playing) {
        hint = gettext('Following you along with the backing track.');
    } else if (following && mode === 'listen') {
        hint = micProblem(live.micState) || (live.chord ? null : gettext('Start playing and the marker finds where you are.'));
    } else if (following && mode === 'tap') {
        hint = gettext('Arrow keys, a page-turner pedal or the buttons move the marker.');
    } else {
        hint = gettext('Follow along and the song keeps your place, with the next chord ready.');
    }

    return (
        <div className="chords-panel practice-tool">
            <div className="chords-now">
                {now && <ChordDiagram
                    chord={chordsByName[now]}
                    name={now}
                    className="large"
                    label={placed ? gettext('Now') : gettext('First chord')}
                    onClick={() => hear(now)}
                    hint={placed ? undefined : 'N'}
                />}
                {next && <ChordDiagram
                    chord={chordsByName[next]}
                    name={next}
                    className="next"
                    label={gettext('Next')}
                    onClick={() => hear(next)}
                    hint={placed ? 'N' : undefined}
                />}
            </div>

            {!playing &&
                <div className="practice-row">
                    <button
                        type="button"
                        className={classNames('practice-pill', 'practice-pill--strong', { active: following })}
                        onClick={following ? track.stopFollowing : track.startFollowing}
                    >
                        {following ? gettext('Stop following') : gettext('Follow me')}
                        <KeyHint k={spaceLabel()} />
                    </button>
                    <Segmented
                        label={gettext('Follow by')}
                        value={settings.followSource}
                        onChange={track.setFollowSource}
                        options={[
                            { value: 'listen', label: gettext('Listening') },
                            { value: 'tap', label: gettext('Tapping') },
                        ]}
                    />
                    {following && mode === 'listen' && live.micState === 'on' && <MicLevel level={live.level} />}
                </div>
            }
            {mode === 'tap' &&
                <div className="practice-row">
                    <button type="button" className="practice-step" onClick={() => track.step(-1)} aria-label={gettext('Previous chord')}>‹<KeyHint k="←" /></button>
                    <button type="button" className="practice-step" onClick={() => track.step(1)} aria-label={gettext('Next chord')}>›<KeyHint k="→" /></button>
                </div>
            }
            {hint && <p className="practice-hint">{hint}</p>}
            {!track.isSubscriber && settings.followSource === 'listen' && !following &&
                <p className="practice-hint practice-subscribe">{gettext('Following by listening is for subscribers. Tapping is free.')}</p>
            }

            <div className="practice-row chords-sounds">
                {names.length > 0 &&
                    <button type="button" className="practice-pill" onClick={() => hear(names[0])}>
                        ▶ {interpolate(gettext('First chord (%s)'), [names[0]])}
                    </button>
                }
                {keyChord &&
                    <button type="button" className={classNames('practice-pill', { active: droneOn })} onClick={onToggleDrone} aria-pressed={droneOn}>
                        {droneOn ? gettext('Stop the drone') : interpolate(gettext('Drone in %s'), [songKey])}
                        <KeyHint k="D" />
                    </button>
                }
            </div>

            <div className="chords-all">
                {track.songChords.map((name, i) => (
                    <ChordDiagram
                        key={name}
                        chord={chordsByName[name]}
                        name={name}
                        shortcut={i < 9 ? String(i + 1) : undefined}
                        className={classNames({ current: placed && name === live.chord })}
                        onClick={() => hear(name)}
                    />
                ))}
            </div>
        </div>
    );
}
